import React from 'react';
import GenericModal from '../../../../ui-components/Modal/GenericModal';
import { GenericTag } from '../../../../ui-components/Tag/GenericTag';
import { SkeletonLine } from '../../../../ui-components/Skeleton/GenericSkeleton';
import { VpcModalProps } from './BGPModalsData';
import { VpcData } from '../BgpVisibilityData';
import { CprEntry } from '../BgpVisibilityData';
import { countDistinctPrefixes, prefixIdentityKey } from '../utils/routeModel';

/**
 * The CIDRs a route table carries, each listed once (CTX-322). Entries arrive one per BGP
 * candidate path, so a prefix learned from two neighbors printed twice in a row in this list —
 * `1.1.1.1/32, 1.1.1.1/32, …`. Deduped on the same `prefix|family` identity the counts use, so
 * the list length always matches the chip beside it.
 */
const distinctPrefixLabels = (prefixes: CprEntry[]): string[] => {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const entry of prefixes) {
    const key = prefixIdentityKey(entry);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    labels.push(entry.prefix);
  }
  return labels;
};

const VpcModal: React.FC<VpcModalProps> = ({ isOpen, onClose, data }) => {
  const isLoading = isOpen && !data;
  // Distinct across the whole VPC, matching the toolbar and the VPC card — not the sum of the
  // route tables' counts, which would double-count a prefix reaching several of them.
  const totalPrefixes = data
    ? countDistinctPrefixes(data.routeTables.flatMap((rt: VpcData['routeTables'][0]) => rt.prefixes))
    : 0;
  const uniqueNeighbors = data ? new Set(data.routeTables.flatMap((rt: VpcData['routeTables'][0]) => rt.prefixes.map((p: CprEntry) => p.neighbor_ip))) : new Set<string>();

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} size="lg" title={data ? `VPC: ${data.vpcId}` : 'VPC'}>
      {isLoading ? (
        <div className="space-y-4" role="status" aria-busy="true">
          <div className="space-y-2">
            <SkeletonLine w="40%" h={16} className="rounded" />
            <SkeletonLine w="100%" h={14} className="rounded" />
            <SkeletonLine w="90%" h={14} className="rounded" />
          </div>
          <div className="space-y-2">
            <SkeletonLine w="30%" h={16} className="rounded" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-3 rounded border border-semantic-border-secondary">
                <SkeletonLine w="50%" h={14} className="rounded" />
                <SkeletonLine w="100%" h={12} className="rounded mt-2" />
              </div>
            ))}
          </div>
        </div>
      ) : !data ? null : (
      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-semantic-text-primary mb-2">VPC Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">VPC ID:</span>
              <span className="text-semantic-text-primary font-medium">{data.vpcId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">Route Tables:</span>
              <span className="text-semantic-text-primary font-medium">{data.routeTables.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">Total Prefixes:</span>
              <span className="text-semantic-text-primary font-medium">{totalPrefixes}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">Unique Neighbors:</span>
              <span className="text-semantic-text-primary font-medium">{uniqueNeighbors.size}</span>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-semibold text-semantic-text-primary mb-2">Route Tables</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {data.routeTables.map((rt: VpcData['routeTables'][0]) => {
              const labels = distinctPrefixLabels(rt.prefixes);
              return (
                <div key={rt.route_table_id} className="p-3 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-semantic-text-primary">{rt.route_table_id}</span>
                    <GenericTag
                      label={`${labels.length} prefix${labels.length !== 1 ? 'es' : ''}`}
                      variant="neutral"
                      size="small"
                    />
                  </div>
                  <div className="text-xs text-semantic-text-secondary">
                    Prefixes: {labels.join(', ')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      )}
    </GenericModal>
  );
};

export default VpcModal;
